import React, { useState } from "react";
import { Label } from "flowbite-react";

interface SearchWithDropdownProps {
  id?: string;
  label: string;
  options: { value: string; label: string }[];
  placeholder: string;
  onSearch: (query: string) => void;
  onChange: (selectedOption: { value: string; label: string } | null) => void;
  value: string; // Controlled value from parent
  errMsg?: string;
}

const SearchWithDropdown: React.FC<SearchWithDropdownProps> = ({
  id,
  label,
  options,
  placeholder,
  onSearch,
  onChange,
  value,
  errMsg,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onSearch(inputValue); // Trigger onSearch with the entered text
    setIsDropdownOpen(true); // Open the dropdown to show matching results
  };

  const handleOptionSelect = (option: { value: string; label: string }) => {
    onChange(option); // Update client_id with the selected option
    setIsDropdownOpen(false); // Close dropdown after selection
  };

  return (
    <div className="mb-4">
      <Label htmlFor={id}>{label}</Label>
      <div className="mt-2 flex">
        <div className="relative">
          <button
            id={`${id}-dropdown-button`}
            type="button"
            className="z-10 inline-flex items-center rounded-s-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-700"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            {options.find((option) => option.value === value)?.label ||
              "Select"}
            <svg
              className="ms-2 h-3 w-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 10 6"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 4 4 4-4"
              />
            </svg>
          </button>
          {isDropdownOpen && (
            <div
              id={`${id}-dropdown`}
              className="absolute z-10 max-h-52 w-52 overflow-y-auto rounded-lg bg-white shadow-sm dark:bg-gray-700"
            >
              {options.length > 0 ? (
                <ul className="rounded-lg border border-gray-300 py-2 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-200">
                  {options.map((option) => (
                    <li key={option.value}>
                      <button
                        type="button"
                        className="inline-flex w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                        onClick={() => handleOptionSelect(option)}
                      >
                        {option.label}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No results found
                </div>
              )}
            </div>
          )}
        </div>
        <div className="relative w-full">
          <input
            type="search"
            id={id}
            className="z-20 block w-full rounded-e-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500"
            placeholder={placeholder}
            onChange={handleInputChange} // Only trigger search, not selection
          />
        </div>
      </div>
      {errMsg && <p className="mt-2 text-sm text-red-500">{errMsg}</p>}
    </div>
  );
};

export default SearchWithDropdown;
